import {
  DropdownItem,
  DropdownList,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Popover,
  useMixpanel,
  usePopoverState,
  type WatchListRowData,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { memo, useCallback } from 'react';
import { useNavigateToDeepDive } from '../../hooks/useNavigateToDeepDive';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import type { MultilegComboType } from '../MultilegCombo/enums';
import { CreateMultilegDropdownItem } from '../SymbolActionsMenu/CreateMultilegDropdownItem';
import { FavoriteSymbolDropdownItem } from '../SymbolActionsMenu/FavoriteSymbolDropdownItem';

export const WatchlistMenuCellRenderer = memo(function WatchlistMenuCellRenderer({
  data,
}: ICellRendererParams<WatchListRowData>) {
  const mixpanel = useMixpanel();
  const { close: closePopover, ...popover } = usePopoverState({
    placement: 'bottom-end',
    usePortal: true,
    closeOnClickOutside: true,
    trigger: 'click',
    noPaddingAndBorder: true,
    onOpen: () => {
      mixpanel.track(MixpanelEvent.OpenSymbolMenu, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.Watchlist,
      });
    },
  });

  const navigateToDeepDive = useNavigateToDeepDive();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();

  const handleOpenDeepDive = useCallback(() => {
    if (!data?.security) {
      return;
    }
    navigateToDeepDive(data.security, undefined, undefined, undefined);
    closePopover();
  }, [data, navigateToDeepDive, closePopover]);

  const handleCreateMultileg = useCallback(
    (multilegType: MultilegComboType) => {
      if (!data?.security) {
        return;
      }
      mixpanel.track(MixpanelEvent.CreateNewMultileg, {
        [MixpanelEventProperty.Type]: multilegType,
        [MixpanelEventProperty.Source]: MixpanelEventSource.Watchlist,
      });
      navigateToMultilegCombo(multilegType, undefined, undefined, undefined, [data.security.Symbol]);
      closePopover();
    },
    [data, mixpanel, navigateToMultilegCombo, closePopover]
  );

  if (!data) {
    return null;
  }

  const { security } = data;

  return (
    <Popover {...popover}>
      <IconButton
        data-testid="watchlist-menu"
        dim
        ghost
        icon={IconName.DotsVertical}
        size={FormControlSizes.Small}
        aria-label="Open Watchlist row menu"
      />
      <DropdownList style={{ width: '220px' }}>
        <FavoriteSymbolDropdownItem
          security={security}
          source={MixpanelEventSource.Watchlist}
          onAfterAction={closePopover}
        />

        <DropdownItem startIcon={IconName.Deepdive} onClick={handleOpenDeepDive}>
          Open DeepDive
        </DropdownItem>

        <CreateMultilegDropdownItem security={security} onCreateMultileg={handleCreateMultileg} />
      </DropdownList>
    </Popover>
  );
});
